import React, { useEffect, useRef, useState } from 'react';
import './ReviewTrain.css';

/* Real, verified reviews only — exact excerpts from each public source, the
   same five the /landing page shows. Do not add invented quotes. The sketch
   avatars are illustrations, not reviewer portraits (said in the footnote). */
export const TRAIN_REVIEWS = [
  { name: 'Priya', source: 'Product Hunt', avatar: '/review-avatars/review-sketch-a.webp',
    url: 'https://www.producthunt.com/products/shakespeare-ai/reviews?review=583571',
    quote: 'After joining I upgraded to test other features and the experience has been amazing so far, plus the team is super responsive to feature requests.' },
  { name: 'RahulK', source: 'Product Hunt', avatar: '/review-avatars/review-sketch-b.webp',
    url: 'https://www.producthunt.com/products/shakespeare-ai?launch=shakespeareai-2-0',
    quote: 'I like the flexibility of chapter edits when I can tailor the direction mid-way.' },
  { name: 'Maria Cristina', source: 'There’s An AI For That', avatar: '/review-avatars/review-sketch-c.webp',
    url: 'https://theresanaiforthat.com/ai/shakespeareai-ca/?comment_id=555654&ref=share',
    quote: 'At first there were a few problems, but the team has been efficient and very quick in solving them for me, even accepting suggestions from me.' },
  { name: 'ecom ninja', source: 'Trustpilot', avatar: '/review-avatars/review-sketch-d.webp',
    url: 'https://www.trustpilot.com/review/shakespeareai.braintastic.ca',
    quote: "The AI kept my characters consistent across 40 chapters, and the plot suggestions felt like they came from an editor who'd actually read my book." },
  { name: 'Fatih Pekçetinöz', source: 'Product Hunt', avatar: '/review-avatars/review-sketch-e.webp',
    url: 'https://www.producthunt.com/products/shakespeare-ai?launch=shakespeareai-2-0',
    quote: '…you know how quickly you lose momentum when you hit a credit wall mid-chapter.' },
];

type Review = typeof TRAIN_REVIEWS[number];

function Card({ review, index, repeat }: { review: Review; index: number; repeat?: boolean }) {
  return (
    <figure className={`rt-card${repeat ? ' rt-repeat' : ''}`}
      {...(repeat ? { 'aria-hidden': true } : { role: 'group', 'aria-roledescription': 'slide', 'aria-label': `Review ${index + 1} of ${TRAIN_REVIEWS.length}` })}>
      <p className="rt-source">{review.source.toUpperCase()} <span aria-hidden="true">↗</span></p>
      <blockquote>“{review.quote}”</blockquote>
      <figcaption>
        <img className="rt-avatar" src={review.avatar} alt="" width={48} height={48} loading="lazy" decoding="async" />
        <div>
          <strong>{review.name}</strong>
          <a href={review.url} target="_blank" rel="noopener noreferrer" tabIndex={repeat ? -1 : undefined}>
            Read original review<span className="rt-sr"> on {review.source} (opens in a new tab)</span> ↗
          </a>
        </div>
      </figcaption>
    </figure>
  );
}

/* Continuous right-to-left train, as on /landing: a second, hidden copy of the
   five cards joins the end to the beginning. Hover (CSS), keyboard focus and
   a reduced-motion preference pause it; nothing else needs user action. */
const ReviewTrain: React.FC = () => {
  const track = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return;
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  // Bring a keyboard-focused card into view by moving the animation to it.
  const onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    setFocused(true);
    const el = track.current;
    const card = (event.target as HTMLElement).closest('.rt-card') as HTMLElement | null;
    const animation = el?.getAnimations?.()[0];
    const loop = (el?.children[TRAIN_REVIEWS.length] as HTMLElement | undefined)?.offsetLeft || 0;
    if (card && animation && loop > 0) {
      animation.currentTime = (card.offsetLeft / loop) * Number(animation.effect?.getTiming().duration || 0);
    }
  };
  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => setFocused(!!track.current?.contains(event.relatedTarget as Node));

  return (
    <div className="review-train" data-motion={reduced || focused ? 'paused' : 'running'}>
      <div className="rt-viewport">
        <div className="rt-track" ref={track} onFocus={onFocus} onBlur={onBlur}>
          {TRAIN_REVIEWS.map((r, i) => <Card review={r} index={i} key={r.name} />)}
          {TRAIN_REVIEWS.map((r, i) => <Card review={r} index={i} key={`${r.name}-repeat`} repeat />)}
        </div>
      </div>
      <p className="rt-footnote">Excerpts are the reviewers’ own words. Experiences reflect their use at the time of review. Sketch avatars are illustrations, not reviewer portraits.</p>
    </div>
  );
};

export default ReviewTrain;
